import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { ProviderMakeGoodPanel } from './ProviderMakeGoodPanel';
import { openDocument } from "@/api/upload";
import { ProviderGrowthReviewPanel } from './ProviderGrowthReviewPanel';
import {
  listProviderVerifications,
  reviewProviderVerification,
  type AdminProviderVerification,
} from "@/api/admin";

/**
 * Provider verification review — the admin half of the provider trust
 * loop. Separate from the D5 unified queue (institutions/EO/interests):
 * provider verifications predate it and live in their own module.
 */
export default function ProviderVerificationsLab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-provider-verifications"],
    queryFn: () => listProviderVerifications({ limit: 50 }),
  });
  const items = data?.data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Provider Verifications"
        subtitle="Approve or reject provider trust documents (business license, identity, certification…)"
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Admin", href: "/admin" },
          { label: "Provider Verifications" },
        ]}
      />

      <ProviderMakeGoodPanel />
      <ProviderGrowthReviewPanel />
      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {!isLoading && items.length === 0 && (
        <p className="text-sm text-gray-500">No pending provider verifications.</p>
      )}

      <ul className="space-y-3">
        {items.map((v) => (
          <ReviewRow key={v.id} v={v} onDone={() => qc.invalidateQueries({ queryKey: ["admin-provider-verifications"] })} />
        ))}
      </ul>
    </div>
  );
}

function ReviewRow({ v, onDone }: { v: AdminProviderVerification; onDone: () => void }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function review(status: "approved" | "rejected") {
    setBusy(true);
    setMsg(null);
    try {
      await reviewProviderVerification(v.id, { status, reviewNotes: notes || undefined });
      setMsg(`${status} ✓`);
      onDone();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Review failed");
    } finally {
      setBusy(false);
    }
  }

  const docs = Array.isArray(v.documents) ? v.documents : [];

  return (
    <li className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium">{v.provider?.name ?? "Provider"}</span>
        <Badge variant="purple">{v.type}</Badge>
        <Badge variant="gray">{v.status}</Badge>
        {v.createdAt && (
          <span className="text-xs text-gray-400">{new Date(v.createdAt).toLocaleDateString()}</span>
        )}
      </div>
      {v.notes && <p className="text-sm text-gray-600">Submitter notes: {v.notes}</p>}
      {docs.length > 0 && (
        <p className="text-sm">
          {/* A document's address needs a token, which a plain link does not send. */}
          {docs.map((d, i) => (
            <button
              key={i}
              type="button"
              onClick={() => openDocument(d).catch((e) => setMsg(e instanceof Error ? e.message : "Could not open"))}
              className="text-primary-600 hover:underline mr-3"
            >
              document {i + 1} ↗
            </button>
          ))}
        </p>
      )}
      <div className="flex gap-2 items-center flex-wrap">
        <input
          className="input flex-1 min-w-48"
          placeholder="Review notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <Button size="sm" disabled={busy} onClick={() => review("approved")}>
          Approve
        </Button>
        <Button size="sm" variant="danger" disabled={busy} onClick={() => review("rejected")}>
          Reject
        </Button>
      </div>
      {msg && <p className="text-sm text-gray-600">{msg}</p>}
    </li>
  );
}
